import {
  MembershipLevel,
  RoleType,
  GenderType,
  CartProductCode,
  MembershipProductType,
  PhoneType,
  MemberAddress,
} from '@ace-clublabs/common-membership-api-types';
import { IGetConfirmationResponse, MSSMembers, MSSProductStatus } from '@lib/types';
import { IFormData, IMemberDetails } from '../state/model';

export class stringHelper {
  static formatMoney(value: string): string {
    let numericValue: number = parseFloat(value); // Convert string to number
    if (Number.isNaN(numericValue)) {
      return '';
    }
    let formattedString: string = numericValue.toFixed(2); // Format with two decimal places
    return `$${formattedString}`;
  }

  static getKeyFromEnum(targetEnum: object, key: string): string {
    return Object.keys(targetEnum)[Object.values(targetEnum).indexOf(key)];
  }
}

export const getMembershipTitle = (
  memberDetails: IMemberDetails,
  membershipLevel?: MembershipLevel,
  confirmation?: IGetConfirmationResponse,
  formData?: IFormData
) => {
  const confirmationProducts = confirmation?.products || [];
  const formDataProducts = formData?.products || [];
  const memberDetailsProducts = memberDetails?.products || [];
  const products = [...confirmationProducts, ...formDataProducts, ...memberDetailsProducts];

  const rvRemoved = products.some(
    ({ type, status }) => type === MembershipProductType.RV && status === MSSProductStatus.REMOVED
  );

  const rvIncluded =
    products.some(({ type, status }) => type === MembershipProductType.RV && status === MSSProductStatus.INCLUDED) ||
    // check if RV is INCLUDED in memberProducts for the pending membershipLevel in prospectiveMemberRules
    (() => {
      const pendingMembershipLevel = memberDetails?.pendingProducts?.find(
        (product) => product.productType in MembershipLevel
      );
      if (pendingMembershipLevel) {
        const pendingLevel = pendingMembershipLevel.productType as MembershipLevel;
        return !!memberDetails?.prospectiveMemberRules?.memberProducts
          ?.find((product) => product.productType === MembershipProductType.RV)
          ?.productRules?.find(
            (rule) => rule.membershipLevel === pendingLevel && rule.rule === MSSProductStatus.INCLUDED
          );
      }
      return false;
    })();

  const rvAdded =
    products.some(({ type, status }) => type === MembershipProductType.RV && status === MSSProductStatus.ADDED) ||
    // check if RV is just purchased and in pending period
    memberDetails?.pendingProducts?.some((product) => product.productType === MembershipProductType.RV);

  const rvExisting = products.some(
    ({ type, status }) => type === MembershipProductType.RV && status === MSSProductStatus.EXISTING
  );

  const hasRV = !rvRemoved && (rvIncluded || rvAdded || rvExisting);

  switch (membershipLevel) {
    case MembershipLevel.CLASSIC:
      return hasRV ? 'Classic RV' : 'Classic';
    case MembershipLevel.PLUS:
      return hasRV ? (
        <>
          Plus<sup>®</sup> RV
        </>
      ) : (
        <>
          Plus<sup>®</sup>
        </>
      );

    case MembershipLevel.PREMIER:
      return hasRV ? (
        <>
          Premier<sup>®</sup> RV
        </>
      ) : (
        <>
          Premier<sup>®</sup>
        </>
      );

    case MembershipLevel.CLASSIC_RV:
      return 'Classic RV';
    case MembershipLevel.PLUS_RV:
      return (
        <>
          Plus<sup>®</sup> RV
        </>
      );
    case MembershipLevel.PREMIER_RV:
      return (
        <>
          Premier<sup>®</sup> RV
        </>
      );
    default:
      return '';
  }
};

export const formatMembershipNumber = (membershipNumber?: string) => {
  if (!membershipNumber) {
    return '';
  }

  const prefix = membershipNumber.slice(0, 3);
  const clubCode = membershipNumber.slice(3, 6);

  return `${prefix} ${clubCode} ${membershipNumber.slice(6)}`;
};

export const ROLE_TYPE_MAP = {
  [RoleType.PRIMARY]: 'Primary member',
  [RoleType.ADULT_ASSOCIATE]: 'Adult associate',
  [RoleType.DEPENDENT]: 'Dependent associate',
  [RoleType.ASSOCIATE]: 'Associate',
};

export const GENDER_CODE_MAP = {
  [GenderType.FEMALE]: 'Female',
  [GenderType.MALE]: 'Male',
  [GenderType.NON_BINARY]: 'Nonbinary',
};

export const capitalizeString = (string: string | undefined) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const validateEmail: (email: string | undefined) => boolean = (email) => {
  if (!email || (email?.trim() ?? '').length === 0) {
    return false;
  } else {
    const emailRegex = /^\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b$/i;
    if (emailRegex.test(email.toUpperCase()) === false) {
      return false;
    }
  }
  return true;
};

export const isGuid = (value: string): boolean => {
  const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89ab][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
  return regex.test(value);
};

export const getPromotionTitle = (itemCode?: CartProductCode, inRenewal?: boolean, fullDiscount?: boolean) => {
  switch (itemCode) {
    case CartProductCode.SERVICELEVEL:
      switch (inRenewal) {
        case true:
          return 'Renewal discount';
        default:
          return 'Membership discount';
      }

    case CartProductCode.ADMISSION:
      return 'Waived admission fee';

    case CartProductCode.RV:
      return 'RV/motorcycle discount';

    case CartProductCode.FAMILYCOST:
      switch (fullDiscount) {
        case true:
          return 'Waived family upgrade fee';
        case false:
          return 'Family upgrade discount';
        default:
          return 'Family upgrade discount';
      }
    case CartProductCode.AUTOPAY_DISCOUNT:
      return 'Auto Pay discount';

    //associates discounts are handled separately

    default:
      return '';
  }
};

export const getMemberNumber = (member: MSSMembers, phoneType: PhoneType) => {
  const numberInfo = member.phoneNumbers?.find((phone) => phone.type === phoneType);
  const memberNumber =
    numberInfo?.areaCode && numberInfo?.prefix && numberInfo?.areaCode
      ? `(${numberInfo.areaCode}) ${numberInfo.prefix}-${numberInfo.suffix}`
      : '';
  return memberNumber;
};

export function getAddressStr(address: MemberAddress | undefined) {
  if (!address) return '';

  const { addressLine1, addressLine2, city, state, zip } = address;
  const line2 = addressLine2 || '';
  const PO_BOX_REGEX: RegExp = /(p\.?\s*o\.?\s*box)/i;

  const toTitleCase = (str: string | undefined) => {
    if (!str) {
      return '';
      // Special handling for 'P.O. Box' variations.
    } else if (PO_BOX_REGEX.test(str)) {
      return str.replace(PO_BOX_REGEX, 'P.O. Box').trim();
      // Otherwise use title case normally.
    } else {
      return str
        .toLowerCase()
        .replace(/\b\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1))
        .trim();
    }
  };

  return `${toTitleCase(addressLine1)} ${toTitleCase(line2)}\n${toTitleCase(city)}, ${state?.toUpperCase()} ${zip}`;
}

export function kebabCaseStr(title: string) {
  return title
    ?.toLowerCase()
    ?.replace(/[^a-zA-Z ]/g, '')
    ?.replace(/\s+/g, '-');
}

export const promotionalLabelMap = {
  [MembershipLevel.CLASSIC]: 'Essential',
  [MembershipLevel.CLASSIC_RV]: 'Essential',
  [MembershipLevel.PLUS]: 'Best value',
  [MembershipLevel.PLUS_RV]: 'Best value',
  [MembershipLevel.PREMIER]: 'Most exclusive',
  [MembershipLevel.PREMIER_RV]: 'Most exclusive',
};
